import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from './http.service';

export interface TrustProfile {
  status: 'standard' | 'trusted' | 'review';
  label: string;
  score: number;
  accountAgeDays?: number;
  deliveredOrders?: number;
  deviceCount?: number;
  perks: { fasterCheckout: boolean; digitalDeliveryExempt: boolean };
  reasons: string[];
}

export interface AuthorizedDevice {
  _id: string;
  label: string;
  browser: string;
  os: string;
  deviceType: string;
  approximateLocation?: string;
  ipAddress?: string;
  isCurrent: boolean;
  lastActiveAt: string;
}

export interface LoginActivityRow {
  _id: string;
  success: boolean;
  blocked: boolean;
  browser?: string;
  deviceType?: string;
  approximateLocation?: string;
  ipAddress?: string;
  reason?: string;
  createdAt: string;
}

export interface AccountSecurityHub {
  trustProfile: TrustProfile;
  devices: AuthorizedDevice[];
  loginLogs: LoginActivityRow[];
  twoFactorEnabled: boolean;
  authenticatorEnabled: boolean;
  stats?: {
    deviceCount: number;
    loginCount: number;
    failedLoginCount: number;
  };
}

@Injectable({ providedIn: 'root' })
export class AccountSecurityService {
  constructor(private http: HttpService) {}

  getHub(): Observable<{ success: boolean; data: AccountSecurityHub }> {
    return this.http.get('auth/account-security');
  }

  revokeDevice(deviceId: string): Observable<{ success: boolean; message: string }> {
    return this.http.post(`auth/devices/${encodeURIComponent(deviceId)}/revoke`, {});
  }

  sendStepUpCode(orderId: string): Observable<{
    success: boolean;
    message: string;
    data?: { phoneLastFour?: string; devCode?: string };
  }> {
    return this.http.post(`orders/me/${encodeURIComponent(orderId)}/step-up/send-code`, {});
  }

  verifyStepUp(orderId: string, code: string): Observable<{ success: boolean; message: string }> {
    return this.http.post(`orders/me/${encodeURIComponent(orderId)}/step-up/verify`, { code });
  }

  getSellerHub(): Observable<{
    success: boolean;
    data: Pick<AccountSecurityHub, 'devices' | 'loginLogs' | 'twoFactorEnabled' | 'authenticatorEnabled' | 'stats'>;
  }> {
    return this.http.get('seller/account-security', { deviceHash: getDeviceHash() }, { includeSellingMode: false });
  }

  syncSellerDevice(): Observable<{ success: boolean; message: string }> {
    return this.http.post(
      'seller/account-security/sync-device',
      { deviceHash: getDeviceHash() },
      { includeSellingMode: false },
    );
  }

  revokeSellerDevice(deviceId: string): Observable<{ success: boolean; message: string }> {
    return this.http.post(
      `seller/devices/${encodeURIComponent(deviceId)}/revoke`,
      { deviceHash: getDeviceHash() },
      { includeSellingMode: false },
    );
  }
}

export function getDeviceHash(): string {
  if (typeof localStorage === 'undefined') return '';
  let id = localStorage.getItem('mb_device_id');
  if (!id) {
    id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `dev-${Date.now()}`;
    localStorage.setItem('mb_device_id', id);
  }
  return id;
}

export function extractCreatedOrders(response: unknown): Record<string, unknown>[] {
  const r = response as Record<string, unknown>;
  if (Array.isArray(r?.['orders'])) return r['orders'] as Record<string, unknown>[];
  if (r?.['_id']) return [r];
  return [];
}

export function orderNeedsStepUp(order: Record<string, unknown> | null | undefined): boolean {
  if (!order) return false;
  const risk = order['riskAssessment'] as Record<string, unknown> | undefined;
  const checkout = order['checkoutVerification'] as Record<string, unknown> | undefined;
  if (risk?.['state'] === 'REJECT') return false;
  if (checkout?.['stepUpVerifiedAt']) return false;
  return risk?.['state'] === 'CHALLENGE' || checkout?.['stepUpRequired'] === true;
}

export function orderWasRejected(order: Record<string, unknown> | null | undefined): boolean {
  const risk = order?.['riskAssessment'] as Record<string, unknown> | undefined;
  return risk?.['state'] === 'REJECT';
}
